import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Power } from "lucide-react";
import { api } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export default function MaintenanceToggle() {
  const [on, setOn] = useState(null);
  const [ask, setAsk] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/status").then(({ data }) => setOn(!!data.maintenance)).catch(() => setOn(false));
  }, []);

  async function save(next) {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/maintenance", { enabled: next });
      setOn(!!data.maintenance);
      toast.success(next ? "Página pausada (modo mantenimiento)" : "Página activa de nuevo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el modo mantenimiento");
    } finally {
      setSaving(false);
      setAsk(false);
    }
  }

  if (on === null) return null;
  return (
    <div data-testid="maintenance-toggle-card" className={`mb-6 rounded-xl border p-4 flex items-center gap-3 ${on ? "border-rose-500/40 bg-rose-500/10" : "border-white/10 bg-[#0d0d0d]"}`}>
      <Power className={`w-5 h-5 ${on ? "text-rose-400" : "text-emerald-400"}`} />
      <div className="flex-1">
        <div className="font-semibold text-white">{on ? "Página en mantenimiento" : "Página activa"}</div>
        <div className="text-xs text-neutral-400" data-testid="maintenance-status">
          {on ? "Los clientes ven la pantalla de mantenimiento. Solo los admins pueden usarla." : "Activá para pausar la página a todos los clientes."}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={saving}
        onClick={() => (on ? save(false) : setAsk(true))}
        data-testid="maintenance-switch"
        className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border ${on ? "bg-emerald-500 text-black border-emerald-400 hover:bg-emerald-400" : "bg-[#ff4500] text-black border-[#ff4500] hover:bg-[#ff5a1f]"}`}
        style={{ transition: "background-color 160ms ease" }}
      >
        {saving ? "Guardando..." : on ? "Reactivar página" : "Pausar página"}
      </button>
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent className="bg-[#0a0a0a] border-[#ff4500]/40 text-white" data-testid="maintenance-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Pausar la página?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              Los clientes verán la pantalla de mantenimiento y no podrán comprar hasta que la vuelvas a activar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="maintenance-cancel" className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="maintenance-confirm" disabled={saving} onClick={(e) => { e.preventDefault(); save(true); }} className="bg-[#ff4500] text-black hover:bg-[#ff5a1f]">
              {saving ? "Pausando..." : "Sí, pausar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
